import { VOIP_CALL_QUALITY_THRESHOLDS } from 'Constants/env';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { CollectedStats } from '../../index.types';
import { calculatePacketLossRate } from '../../utils';
import {
  VoipCallConnectionEvent,
  VoipCallConnectionState,
} from './index.types';

/**
 * VoipQualityAdapter is a class that analyzes the quality of a voip connection
 */
export class VoipQualityAdapter {
  constructor(
    private callId: string,
    private personId: string,
    private isOnline: boolean,
    private peerConnection?: RTCPeerConnection | null
  ) {}

  /**
   * Determine the connection state of the voip connection
   * @returns The connection state
   */
  public determineConnectionState(): VoipCallConnectionState {
    try {
      if (!this.isOnline) {
        this.trackConnectionEvent('on_call_no_connection');
        return 'off';
      }

      if (!this.peerConnection) {
        this.trackConnectionEvent('on_call_no_connection');
        return 'off';
      }

      const connectionState = this.peerConnection.connectionState;
      const iceConnectionState = this.peerConnection.iceConnectionState;

      if (!connectionState || !iceConnectionState) {
        console.warn('VoipQualityAdapter: Invalid connection states:', {
          connectionState,
          iceConnectionState,
        });
        return 'off';
      }

      const isConnectionFailed =
        connectionState === 'failed' || iceConnectionState === 'failed';

      const isConnectionDisconnected =
        connectionState === 'disconnected' ||
        iceConnectionState === 'disconnected';

      if (isConnectionFailed || isConnectionDisconnected) {
        this.trackConnectionEvent('on_call_no_connection');
        return 'off';
      }

      return 'stable';
    } catch (error) {
      console.error(
        'VoipQualityAdapter: Error determining connection state:',
        error
      );
      return 'off'; // Safe default
    }
  }

  public analyzeQuality(
    collectedStats: CollectedStats,
    metricsHistory: CollectedStats[]
  ): VoipCallConnectionState {
    if (!collectedStats) {
      console.warn('VoipQualityAdapter: No stats collected');
      return 'stable';
    }

    const connectionState = this.determineConnectionState();
    if (connectionState === 'off') return 'off';

    // If roundTripTime is undefined, we assume it as invalid
    const rtt = collectedStats.basicStats.roundTripTime
      ? collectedStats.basicStats.roundTripTime * 1000
      : undefined;
    // If jitter is undefined, we assume it as 0
    const jitter = !isNullOrUndefined(collectedStats.basicStats.jitter)
      ? collectedStats.basicStats.jitter * 1000
      : 0;
    // If packetsLost or packetsReceived are undefined, we assume it as 0
    const packetLossRate = calculatePacketLossRate(metricsHistory);
    // If availableBitrate is undefined, we assume it as invalid
    const rawBitrate = collectedStats.basicStats?.availableBitrate;

    // Check for POOR connection
    // Note: Only check bitrate if it's > 0 (available), otherwise ignore it
    const isPoorConnection =
      rtt > VOIP_CALL_QUALITY_THRESHOLDS.UNSTABLE.RTT ||
      jitter > VOIP_CALL_QUALITY_THRESHOLDS.UNSTABLE.JITTER ||
      packetLossRate > VOIP_CALL_QUALITY_THRESHOLDS.UNSTABLE.PACKET_LOSS ||
      (rawBitrate > 0 &&
        rawBitrate < VOIP_CALL_QUALITY_THRESHOLDS.UNSTABLE.BITRATE);

    if (isPoorConnection) {
      this.trackConnectionEvent('on_call_poor_connection');
      return 'poor';
    }

    // Check for UNSTABLE connection
    // Note: Only check bitrate if it's > 0 (available), otherwise ignore it
    const isUnstableConnection =
      rtt > VOIP_CALL_QUALITY_THRESHOLDS.STABLE.RTT ||
      jitter > VOIP_CALL_QUALITY_THRESHOLDS.STABLE.JITTER ||
      packetLossRate > VOIP_CALL_QUALITY_THRESHOLDS.STABLE.PACKET_LOSS ||
      (rawBitrate > 0 &&
        rawBitrate < VOIP_CALL_QUALITY_THRESHOLDS.STABLE.BITRATE);

    if (isUnstableConnection) {
      return 'unstable';
    }

    return 'stable';
  }

  /**
   * Track the connection event
   * @param eventName - The event name
   */
  private trackConnectionEvent(eventName: VoipCallConnectionEvent): void {
    try {
      if (!this.callId || !this.personId) {
        console.warn(
          'VoipQualityAdapter: Missing callId or personId for event tracking'
        );
        return;
      }

      pushToGTMDataLayer(eventName, {
        callId: this.callId,
        personId: this.personId,
        timestamp: new Date().toISOString(),
        connectionState: this.peerConnection?.connectionState,
        iceConnectionState: this.peerConnection?.iceConnectionState,
      });
    } catch (error) {
      console.error(
        'VoipQualityAdapter: Error tracking connection event:',
        error
      );
    }
  }
}
